import { createStyles, makeStyles, Theme } from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import {
	BOX_SHADOWS,
	DATA_TYPES,
	FONTS,
	SECONDARY_COLOR,
	SORT_TYPES,
} from "../../constants";
import { addAlpha, calcPercentile, safeTrim } from "../../helpers/DataHelpers";
import { formatNumber } from "../../helpers/FormatNumber";
import Dataset from "../../MachineLearning/Dataset";
import { stateType, stateTypeF } from "../../state";
import { BoxHeader } from "../Common/BoxHeader";
import styles from "../DataCleaning/scroll.module.css";

const CELL_HEIGHT = "30px";
const ROWS_PER_PAGE = 50;

const useStyles = makeStyles((theme: Theme) => {
	return createStyles({
		container: {
			display: "flex",
			flexDirection: "column",
			width: "100%",
			backgroundColor: "white",
			boxShadow: BOX_SHADOWS,
			borderRadius: "10px",
			height: "500px",
			transition: "0.5s",
			overflow: "hidden",
		},
		scrollContainer: {
			display: "flex",
			flexDirection: "column",
			backgroundColor: "white",
			transition: "0.5s",
			width: "100%",
		},
		line: {
			width: "100%",
			height: "1px",
			backgroundColor: "rgba(0,0,0,0.2)",
			position: "relative",
			marginTop: "-10px",
		},
		row: {
			display: "flex",
			flexDirection: "row",
			margin: "10px",
			marginLeft: "0px",
			marginTop: "0px",
		},
		rowChecked: {
			display: "flex",
			flexDirection: "row",
			margin: "10px",
			marginLeft: "0px",
			paddingLeft: "0px",
			marginTop: "0px",
		},
		rowWithLine: {
			display: "flex",
			flexDirection: "column",
			"&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
		},
		rowWithLineChecked: {
			display: "flex",
			flexDirection: "column",
			backgroundColor: addAlpha(SECONDARY_COLOR, 0.2),
		},
		header: {
			display: "flex",
			flexDirection: "row",
			margin: "10px",
			marginLeft: "0px",
			cursor: "pointer",
		},
		cell: {
			fontFamily: FONTS,
			fontSize: "14px",
			lineHeight: "30px",
		},
		headerCell: {
			fontFamily: FONTS,
			fontSize: "14px",
			"&:hover": { color: "rgba(0,0,0,.6)" },
		},
		sortArrow: {
			height: "17px",
			width: "17px",
			marginTop: "2px",
			transition: "0.3s",
		},
		footerContainer: {
			display: "flex",
			flexDirection: "row",
			width: "100%",
			justifyContent: "right",
			marginTop: "10px",
			marginBottom: "10px",
		},
		pageArrowLeft: {
			width: "17px",
			height: "17px",
			marginTop: "2px",
			transform: "rotate(180deg)",
			"&:hover": { transform: "scale(1.2) rotate(180deg)" },
		},
		pageArrowRight: {
			width: "17px",
			height: "17px",
			marginTop: "2px",

			"&:hover": { transform: "scale(1.2)" },
		},
		pageArrowContainer: {
			width: "30px",
			height: "30px",
			cursor: "pointer",
		},
		headerContainer: { display: "flex" },
		filterButtonContainer: {
			marginTop: "25px",
			marginRight: "40px",
			marginLeft: "auto",
			cursor: "pointer",
		},
		filterButton: { "&:hover": { transform: "scale(1.05)" } },
		dropDown: {
			width: "140px",
			height: "30px",
			border: "solid",
			marginLeft: "20px",
			borderRadius: "5px",
			borderColor: "rgba(0,0,0,0.2)",
			borderWidth: "2px",
			marginTop: "-5px",
			display: "flex",
			flexDirection: "row",
			transition: "0.3s",
			cursor: "pointer",
			backgroundColor: "white",
			"&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
		},
	});
});

export function makeCleanTableData(dataset: Dataset) {
	let rowCount = Array(dataset.columns[0].vectorizedData.length)
		.fill(0)
		.map((_, i) => i + 1);
	let pageData: any[][] = [rowCount];
	for (let col of dataset.columns) {
		let tmp;
		try {
			tmp = dataset.data[col.columnName];
		} catch {
			let a = 1;
		}

		if (col.availableColumnTypes.includes(DATA_TYPES.number)) {
			tmp = tmp.map((val) => Number.parseFloat(val));
		}
		pageData.push(tmp);
	}
	pageData = pageData[0].map((_, colIndex) =>
		pageData.map((row) => row[colIndex])
	);
	return pageData;
}

export function Line() {
	return (
		<div
			style={{
				width: "100%",
				height: "1px",
				backgroundColor: "rgba(0,0,0,0.2)",
				position: "relative",
				marginTop: "-10px",
				overflow: "hidden",
			}}
		></div>
	);
}

type tableHeaderInputs = {
	state: stateTypeF;
	headers: string[];
	colWidths: string[];
};
function TableHeader({ state, headers, colWidths }: tableHeaderInputs) {
	const classes = useStyles();
	const rowNum = -1;
	let isChecked = rowNum in state.saveState.cleanData.cleanRowsSelected;

	return (
		<div
			className={classes.rowWithLine}
			style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
		>
			<div className={classes.header}>
				<div
					className={classes.cell}
					style={{
						height: "40px",
						marginTop: "-10px",
						marginBottom: "-5px",
					}}
				></div>

				{headers.map((colName, i) => (
					<TableHeadCell
						colName={colName}
						width={colWidths[i]}
						height={CELL_HEIGHT}
						index={i}
						state={state}
					></TableHeadCell>
				))}
			</div>
			<Line></Line>
		</div>
	);
}

type tableRowType = { state: stateTypeF; row: any[]; colWidths: string[] };
export function TableRow({ state, row, colWidths }: tableRowType) {
	const classes = useStyles();
	const rowNum = row[0];
	let isChecked = rowNum in state.saveState.cleanData.cleanRowsSelected;

	let rowClass = isChecked ? classes.rowWithLineChecked : classes.rowWithLine;

	return (
		<div className={rowClass}>
			<div className={classes.row}>
				<div
					className={classes.cell}
					style={{
						height: "40px",
						marginTop: "-5px",
						marginBottom: "-5px",
					}}
				></div>
				{row.map((val, i) => (
					<TableCell
						value={val}
						width={colWidths[i]}
						height={CELL_HEIGHT}
						index={i}
					></TableCell>
				))}
			</div>
			<Line></Line>
		</div>
	);
}

type tableCellType = {
	value: string | number;
	width: string;
	height: string;
	index: number;
	state?: stateTypeF;
};

type tableHeadCellType = {
	colName: string;
	width: string;
	height: string;
	index: number;
	state: stateTypeF;
};

function TableCell({ value, width, height, index }: tableCellType) {
	const classes = useStyles();
	const isFirst = index === 0;
	const isLast = index === 11;
	return (
		<div
			className={classes.cell}
			style={{
				height: height,
				width: width,
				marginLeft: index === 0 ? "0px" : "20px",
			}}
		>
			<div
				style={{
					textAlign: isFirst ? "left" : "right",
					marginLeft: isFirst ? "10px" : "0px",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{value}
			</div>
		</div>
	);
}

function TableHeadCell({
	colName,
	width,
	height,
	index,
	state,
}: tableHeadCellType) {
	const classes = useStyles();
	const isFirst = index === 0;
	const isSelected = false;

	return (
		<div
			className={classes.headerCell}
			style={{
				height: height,
				width: width,
				marginLeft: index === 0 ? "0px" : "20px",
			}}
		>
			<div
				style={{
					justifyContent: isFirst ? "left" : "right",
					display: "flex",
				}}
			>
				{isFirst ? (
					<div></div>
				) : (
					<div
						style={{
							height: "0px",
						}}
					>
						{isSelected ? (
							<div
								style={{
									width: "0px",
									marginRight: "10px",
									marginLeft: "-10px",
								}}
							>
								<ArrowDownwardIcon
									className={classes.sortArrow}
									style={{
										transform:
											state.cleanTableColSort[1] ===
											SORT_TYPES.asc
												? "rotate(-180deg)"
												: "rotate(0deg)",
									}}
								></ArrowDownwardIcon>
							</div>
						) : (
							<div></div>
						)}
					</div>
				)}
				<div
					style={{
						fontWeight: "bold",
						marginLeft: isFirst ? "10px" : "10px",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{colName}
				</div>
				{!isFirst ? (
					<div></div>
				) : (
					<div style={{ height: "0px" }}>
						{isSelected ? (
							<div
								style={{
									width: "0px",
									marginLeft: "10px",
									marginRight: "-10px",
								}}
							>
								<ArrowDownwardIcon
									className={classes.sortArrow}
									style={{
										transform:
											state.cleanTableColSort[1] ===
											SORT_TYPES.asc
												? "rotate(-180deg)"
												: "rotate(0deg)",
									}}
								></ArrowDownwardIcon>
							</div>
						) : (
							<div></div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export function calcColumnWidths(
	header: string,
	data: any[],
	sentTextCol: string
) {
	const headerWidth = header.length;
	let subData = data.slice(0, 1000);
	let lengths = [];
	for (let val of subData) {
		try {
			lengths.push(val.toString().length);
		} catch {
			lengths.push(0);
		}
	}

	let dataWidth = calcPercentile(lengths, 0.9, DATA_TYPES.number);

	let maxWidth = header === sentTextCol ? 500 : 250;

	let colWidths = Math.max(headerWidth, dataWidth);
	colWidths = Math.max(Math.min(colWidths * 9 + 20, maxWidth), 80);
	if (header.startsWith("Sentiment: ")) {
		colWidths = 150;
	}

	return colWidths + "px";
}

function ScrollTable({ state }: stateType) {
	let headers = state.cleanData.headers;

	let page = state.cleanTablePage;

	let ndx = state.saveState.sentiment.varSelected;
	let sentTextCol = state.saveState.dataset.nlpColumns[ndx];
	let sentColName = "Sentiment: " + sentTextCol;
	let index = headers.indexOf(sentColName);
	let index2 = headers.indexOf(sentTextCol);
	function reCalcSentTable() {
		let tbleData = state.cleanFilteredData;
		let sorted = tbleData.sort((a, b) => b[index] - a[index]);
		sorted = sorted.filter(
			(v) => safeTrim(v[index2]).split(" ").length > 8
		);
		let tmp1 = sorted.slice(0, 50);
		let tmp2 = sorted.slice(sorted.length - 50, sorted.length);
		let tableData = [...tmp1, ...tmp2];
		state.setSentTableData(tableData);
	}

	if (state.reCaclSentTable) {
		state.setReCalcSentTable(false);
		reCalcSentTable();
	}
	let subData = state.sentTableData;
	if (subData === undefined) {
		return <div></div>;
	}

	let pageData = subData.slice(
		page * ROWS_PER_PAGE,
		(page + 1) * ROWS_PER_PAGE
	);

	// Reorder Columns
	const firstRows = ["Row Number", sentColName, sentTextCol];
	const indexes = firstRows.map((r) => headers.indexOf(r));
	for (let i = 0; i < headers.length; i++) {
		if (indexes.includes(i)) {
			continue;
		}
		indexes.push(i);
	}
	for (let i = 0; i < pageData.length; i++) {
		let tmp = pageData[i].map((v, k) => pageData[i][indexes[k]]);
		pageData[i] = tmp;
	}
	headers = headers.map((v, k) => headers[indexes[k]]);

	const colWidths: string[] = [];
	for (let i = 0; i < headers.length; i++) {
		let row = pageData.map((row) => row[i]);
		colWidths.push(calcColumnWidths(headers[i], row, sentTextCol));
	}
	return (
		<div className={styles.scrollContainer}>
			<div style={{ width: "fit-content", minWidth: "100%" }}>
				<TableHeader
					state={state}
					headers={headers}
					colWidths={colWidths}
				/>
			</div>
			<div
				style={{ width: "fit-content", minWidth: "100%" }}
				className={styles.scrollContainer}
			>
				{pageData.map((row) => (
					<TableRow
						state={state}
						row={row}
						colWidths={colWidths}
					></TableRow>
				))}
			</div>
		</div>
	);
}

function Footer({ state }: stateType) {
	const classes = useStyles();
	let page = state.cleanTablePage;
	if (state.sentTableData === undefined) {
		return <div></div>;
	}
	let nRows = formatNumber(state.sentTableData.length);
	let start = "0";
	let end = "0";
	if (nRows !== "0") {
		start = formatNumber(page * ROWS_PER_PAGE + 1);
		end = formatNumber(
			Math.min(
				(page + 1) * ROWS_PER_PAGE,
				Number.parseFloat(nRows.replace(",", ""))
			)
		);
		let numPages = Math.ceil(
			Number.parseFloat(nRows.replace(",", "")) / ROWS_PER_PAGE
		);
		if (page > numPages - 1) {
			state.setCleanTablePage(numPages - 1);
		}
	}

	function rightArrowClick() {
		let nPages = Math.ceil(
			Number.parseFloat(nRows.replace(",", "")) / ROWS_PER_PAGE
		);
		if (page < nPages - 1) {
			state.setCleanTablePage(page + 1);
		}
	}

	function leftArrowClick() {
		if (page > 0) {
			state.setCleanTablePage(page - 1);
		}
	}

	return (
		<div className={classes.footerContainer}>
			<div
				style={{
					marginLeft: "20px",
					marginRight: "auto",
					fontFamily: FONTS,
				}}
			></div>
			<div style={{ marginRight: "20px", fontFamily: FONTS }}>
				{" "}
				{start}-{end} of {nRows}
			</div>
			<div
				className={classes.pageArrowContainer}
				onClick={leftArrowClick}
			>
				<ArrowForwardIosIcon className={classes.pageArrowLeft} />
			</div>
			<div
				className={classes.pageArrowContainer}
				style={{ marginRight: "20px" }}
				onClick={rightArrowClick}
			>
				<ArrowForwardIosIcon className={classes.pageArrowRight} />
			</div>
		</div>
	);
}

export function TopSentChart({ state }: stateType) {
	const classes = useStyles();

	if (state.cleanData === undefined) {
		return <div></div>;
	}

	return (
		<div className={classes.container}>
			<div className={classes.headerContainer}>
				<BoxHeader
					title={"Top 50 and Bottom 50 by Sentiment"}
					toolTip={
						"Select any rows you want to exclude from your dataset."
					}
					line={false}
				></BoxHeader>
			</div>
			<ScrollTable state={state}></ScrollTable>
			<Footer state={state}></Footer>
		</div>
	);
}
